import { api } from "../../api";

const createQueryParams = (params, isAdditionalFilters) => {
    if (!params || typeof params !== "object") return "";

    const appliedFilters = Object.keys(params)
        .map((key) => {
            const value = params[key];
            if (value === undefined || value === null || value === "")
                return "";
            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .filter(Boolean)
        .join("&");

    if (!appliedFilters) return "";
    return isAdditionalFilters ? `?${appliedFilters}` : `&${appliedFilters}`;
};

export const adminApi = api.injectEndpoints({
    endpoints: (build) => ({
        getYearConfiguration: build.query({
            query: () => "/admin-module/year-configuration/",
        }),
        getUsers: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/admin-module/user/?${pageParams}${pageSizeParams}${appliedFilters}`;
            },
            providesTags: ["User"],
        }),
        getSectors: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/admin-module/sector/?${pageParams}${pageSizeParams}${appliedFilters}`;
            },
            providesTags: ["Sector"],
        }),
        getCategories: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/admin-module/category/?${pageParams}${pageSizeParams}${appliedFilters}`;
            },
            providesTags: ["Category"],
        }),
        getSubCategories: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/admin-module/sub-category/?${pageParams}${pageSizeParams}${appliedFilters}`;
            },
            providesTags: ["SubCategory"],
        }),
        getTiers: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/admin-module/client-tier/?${pageParams}${pageSizeParams}${appliedFilters}`;
            },
            providesTags: ["Tier"],
        }),
        getTraders: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/admin-module/trader/?${pageParams}${pageSizeParams}${appliedFilters}`;
            },
            providesTags: ["Trader"],
        }),
        getRatingSpreads: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/rating-spread-view/?${pageParams}${pageSizeParams}${appliedFilters}`;
            },
            providesTags: ["RatingSpread"],
        }),
        getClients: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/admin-module/client/?${pageParams}${pageSizeParams}${appliedFilters}`;
            },
            providesTags: ["Client"],
        }),
        createRatingSpread: build.mutation({
            query: ({ data }) => ({
                url: "/research/rating-spread-view/",
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["RatingSpread"],
        }),
        createSector: build.mutation({
            query: ({ data }) => ({
                url: "/admin-module/sector/",
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["Sector", "SectorAssignment"],
        }),
        updateSector: build.mutation({
            query: ({ data }) => ({
                url: `/admin-module/sector/${data.get("id")}/`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: ["Sector"],
        }),
        createCategory: build.mutation({
            query: ({ data }) => ({
                url: "/admin-module/category/",
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["Category"],
        }),
        updateCategory: build.mutation({
            query: ({ data }) => ({
                url: `/admin-module/category/${data.get("id")}/`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: ["Category"],
        }),
        createTier: build.mutation({
            query: ({ data }) => ({
                url: "/admin-module/client-tier/",
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["Tier"],
        }),
        updateTier: build.mutation({
            query: ({ data }) => ({
                url: `/admin-module/client-tier/${data.get("id")}/`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: ["Tier"],
        }),
        getSectorAssignments: build.query({
            query: () => {
                return `/research/sector-assignment/`;
            },
            providesTags: ["SectorAssignment"],
        }),
        getAnalystAssignments: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/analyst-sector-assignment/?${appliedFilters}`;
            },
            providesTags: ["AnalystAssignment"],
        }),
        createAnalystAssignment: build.mutation({
            query: ({ data }) => ({
                url: "/research/analyst-sector-assignment/",
                method: "POST",
                body: data,
            }),
            invalidatesTags: [
                "AnalystAssignment",
                "SectorAssignment",
                "AnalystList",
            ],
        }),
        updateAnalystAssignment: build.mutation({
            query: ({ data }) => ({
                url: `/research/analyst-sector-assignment/${data.id}/`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: [
                "AnalystAssignment",
                "SectorAssignment",
                "AnalystList",
            ],
        }),
        deleteAnalystAssignment: build.mutation({
            query: (id) => ({
                url: `/research/analyst-sector-assignment/${id}/`,
                method: "DELETE",
            }),
            invalidatesTags: ["AnalystAssignment"],
        }),
        createTrader: build.mutation({
            query: ({ data }) => ({
                url: "/admin-module/trader/",
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["Trader"],
        }),
        updateTrader: build.mutation({
            query: ({ data }) => ({
                url: `/admin-module/trader/${data.get("id")}/`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: ["Trader"],
        }),
        createSubCategory: build.mutation({
            query: ({ data }) => ({
                url: "/admin-module/sub-category/",
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["SubCategory"],
        }),
        updateSubCategory: build.mutation({
            query: ({ data }) => ({
                url: `/admin-module/sub-category/${data.get("id")}/`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: ["SubCategory"],
        }),
        createClient: build.mutation({
            query: ({ data }) => ({
                url: "/admin-module/client/",
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["Client"],
        }),
        updateClient: build.mutation({
            query: ({ data }) => ({
                url: `/admin-module/client/${data.id}/`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: ["Client"],
        }),
        deleteClient: build.mutation({
            query: (id) => ({
                url: `/admin-module/client/${id}/`,
                method: "DELETE",
            }),
            invalidatesTags: ["Client"],
        }),
        createUser: build.mutation({
            query: ({ data }) => ({
                url: "/admin-module/user/",
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["User"],
        }),
        updateUser: build.mutation({
            query: ({ data }) => ({
                url: `/admin-module/user/${data.get("id")}/`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: ["User"],
        }),
        deleteUser: build.mutation({
            query: (id) => ({
                url: `/admin-module/user/${id}/`,
                method: "DELETE",
            }),
            invalidatesTags: [
                "User",
                "SectorAssignment",
                "AnalystAssignment",
                "AnalystList",
            ],
        }),
        getRoles: build.query({
            query: () => "/admin-module/roles/",
        }),
        getFileTypeView: build.query({
            query: () => "/admin-module/file-type-view/",
        }),
        getUserById: build.query({
            query: (id) => `/admin-module/user/${id}/`,
        }),
        getFileSearchView: build.mutation({
            query: (data) => ({
                url: "/admin-module/file-search-view/",
                method: "POST",
                body: data,
            }),
        }),
        getFileUploadTracker: build.mutation({
            query: (formData) => ({
                url: "/admin-module/file-upload-tracker/",
                method: "POST",
                body: formData,
            }),
        }),
        getFileSyncView: build.mutation({
            query: (data) => ({
                url: "/admin-module/file-sync-view/",
                method: "POST",
                body: data,
            }),
            async onQueryStarted(arg, { dispatch }) {
                dispatch(api.util.resetApiState());
            },
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetUsersQuery,
    useCreateUserMutation,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useGetRolesQuery,
    useGetFileTypeViewQuery,
    useGetUserByIdQuery,
    useGetFileSearchViewMutation,
    useGetFileUploadTrackerMutation,
    useGetFileSyncViewMutation,
    useGetClientsQuery,
    useGetSectorsQuery,
    useGetCategoriesQuery,
    useGetSubCategoriesQuery,
    useGetTiersQuery,
    useGetTradersQuery,
    useGetRatingSpreadsQuery,
    useCreateRatingSpreadMutation,
    useCreateSectorMutation,
    useUpdateSectorMutation,
    useCreateCategoryMutation,
    useUpdateCategoryMutation,
    useCreateTierMutation,
    useUpdateTierMutation,
    useCreateTraderMutation,
    useUpdateTraderMutation,
    useCreateSubCategoryMutation,
    useUpdateSubCategoryMutation,
    useCreateClientMutation,
    useUpdateClientMutation,
    useDeleteClientMutation,
    useGetSectorAssignmentsQuery,
    useGetAnalystAssignmentsQuery,
    useCreateAnalystAssignmentMutation,
    useUpdateAnalystAssignmentMutation,
    useDeleteAnalystAssignmentMutation,
    useGetYearConfigurationQuery,
} = adminApi;
